import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from '@phosphor-icons/react';
import CountUp from 'react-countup';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../i18n/translations';
import { Button } from './ui/button';

const heroImage = "https://static.prod-images.emergentagent.com/jobs/0d77d791-1090-4f5a-a5b4-c78945c5298b/images/b4bfb769feda05a6b3bce5d52b755ca372f42332845498167b97e44ca5d420ec.png";

const StatItem = ({ value, suffix, label, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay }}
    className="text-center px-6"
  >
    <div className="text-3xl sm:text-4xl font-bold text-white mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
      <CountUp end={value} duration={2.5} suffix={suffix} enableScrollSpy scrollSpyOnce />
    </div>
    <p className="text-xs sm:text-sm text-white/50 uppercase tracking-wider">{label}</p>
  </motion.div>
);

export const HeroSection = () => {
  const { t } = useLanguage();

  const stats = [
    { value: companyInfo.stats.projects, suffix: '+', label: t.hero.stats.projects },
    { value: companyInfo.stats.experience, suffix: '+', label: t.hero.stats.experience },
    { value: companyInfo.stats.engineers, suffix: '', label: t.hero.stats.engineers },
    { value: companyInfo.stats.provinces, suffix: '', label: t.hero.stats.provinces },
  ];

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden -mt-20"
      data-testid="hero-section"
    >
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img src={heroImage} alt="Engineering" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1320]/80 via-[#0B1320]/70 to-[#0B1320]/90" />
      </div>

      {/* Glassmorphism accent */}
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#FF6B00]/10 rounded-full blur-[120px] z-0" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FF6B00]/5 rounded-full blur-[80px] z-0" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
        <div className="max-w-4xl">
          {/* Overline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[#FF6B00] text-xs sm:text-sm font-medium uppercase tracking-[0.3em] mb-6"
          >
            {t.hero.overline}
          </motion.p>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-6"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="hero-title"
          >
            {t.hero.slogan}
          </motion.h1>

          {/* Subtext */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-base sm:text-lg text-white/70 max-w-2xl mb-10 leading-relaxed"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            {t.hero.subtext}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 mb-20"
          >
            <Button
              asChild
              size="lg"
              className="bg-[#FF6B00] hover:bg-[#FF6B00]/90 text-white rounded-none px-8 py-6 text-base font-medium tracking-wide group"
              data-testid="hero-services-btn"
            >
              <Link to="/services">
                {t.hero.cta_services}
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-white/20 text-white hover:bg-white/10 backdrop-blur-sm rounded-none px-8 py-6 text-base font-medium tracking-wide"
              data-testid="hero-contact-btn"
            >
              <Link to="/contact">
                {t.hero.cta_contact}
              </Link>
            </Button>
          </motion.div>
        </div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 sm:p-8"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 divide-x divide-white/10">
            {stats.map((stat, i) => (
              <StatItem key={stat.label} {...stat} delay={0.8 + i * 0.1} />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
